import { useEffect, useState } from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  IconButton,
  MenuItem,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import {
  CHALLENGE_TYPES,
  OPINION_TYPES,
  activityToForm,
  buildActivityPayload,
  emptyActivityForm,
  validateActivityForm,
} from './dailyTasksUtils';

function ActivityFormModal({
  open,
  onClose,
  onSubmit,
  activity,
  group,
  actionOptions = [],
  locked,
  saving,
}) {
  const defaultType = group === 'opinions' ? 'prediction' : 'challenge';
  const [form, setForm] = useState(() => emptyActivityForm(defaultType));
  const [errors, setErrors] = useState([]);

  useEffect(() => {
    if (!open) return;
    if (activity) setForm(activityToForm(activity));
    else setForm(emptyActivityForm(defaultType));
    setErrors([]);
  }, [open, activity, defaultType]);

  const isOpinion = OPINION_TYPES.includes(form.activity_type);
  const typeOptions =
    group === 'opinions'
      ? [
          { value: 'hot_take', label: 'Hot take' },
          { value: 'prediction', label: 'Prediction' },
        ]
      : [
          { value: 'challenge', label: 'Challenge' },
          { value: 'quiz', label: 'Quiz' },
        ];

  const update = (patch) => setForm((prev) => ({ ...prev, ...patch }));
  const updateConfig = (patch) =>
    setForm((prev) => ({ ...prev, config: { ...prev.config, ...patch } }));

  const handleTypeChange = (activity_type) => {
    const next = emptyActivityForm(activity_type);
    setForm({
      ...next,
      title: form.title,
      subtitle: form.subtitle,
      image_url: form.image_url,
      sort_order: form.sort_order,
      action_key: form.action_key,
      navigation_link: form.navigation_link,
      force_correction: form.force_correction,
    });
  };

  const handleSave = () => {
    const errs = validateActivityForm(form);
    setErrors(errs);
    if (errs.length) return;
    onSubmit(buildActivityPayload(form));
  };

  const options = Array.isArray(form.config?.options) ? form.config.options : [];
  const contextImages = Array.isArray(form.config?.context?.images)
    ? form.config.context.images
    : [];
  const bg = Array.isArray(form.config?.background_color)
    ? form.config.background_color
    : ['', ''];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>{activity ? 'Edit activity' : 'Add activity'}</DialogTitle>
      <DialogContent>
        {errors.length > 0 && (
          <Alert severity="error" sx={{ mb: 2, mt: 1 }}>
            {errors.join(' ')}
          </Alert>
        )}
        {isOpinion && (
          <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
            These are Dailies opinion cards (hot takes / predictions), not Pulse arcade prediction
            games. Fans submit via <code>POST /dailies/predictions/submit</code>.
          </Alert>
        )}

        <Grid container spacing={2} sx={{ mt: 0.5 }}>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              label="Activity type"
              value={form.activity_type}
              onChange={(e) => handleTypeChange(e.target.value)}
            >
              {typeOptions.map((opt) => (
                <MenuItem key={opt.value} value={opt.value}>
                  {opt.label}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              fullWidth
              type="number"
              label="Sort order"
              value={form.sort_order}
              onChange={(e) => update({ sort_order: Number(e.target.value) })}
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              select
              fullWidth
              label="Completion mode"
              value={form.completion_mode}
              onChange={(e) => update({ completion_mode: e.target.value })}
              helperText={
                form.completion_mode === 'target'
                  ? 'Reach score / wins / level (target_value + action)'
                  : 'Finishing / submitting once counts'
              }
            >
              <MenuItem value="participation">Participation</MenuItem>
              <MenuItem value="target">Target</MenuItem>
            </TextField>
          </Grid>
          {form.completion_mode === 'target' && (
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                type="number"
                label="Target value"
                value={form.target_value}
                onChange={(e) => update({ target_value: Number(e.target.value) })}
                inputProps={{ min: 1 }}
              />
            </Grid>
          )}
          <Grid item xs={12}>
            <TextField
              fullWidth
              required
              label="Title"
              value={form.title}
              onChange={(e) => update({ title: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Subtitle"
              value={form.subtitle}
              onChange={(e) => update({ subtitle: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Image URL"
              value={form.image_url}
              onChange={(e) => update({ image_url: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              select
              fullWidth
              label="Action key"
              value={form.action_key || ''}
              onChange={(e) => update({ action_key: e.target.value })}
              helperText="From mission actions catalogue"
            >
              <MenuItem value="">— none —</MenuItem>
              {actionOptions.map((a) => (
                <MenuItem key={a.action_key} value={a.action_key}>
                  {a.label || a.action_key}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Navigation link"
              value={form.navigation_link}
              onChange={(e) => update({ navigation_link: e.target.value })}
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Ref type"
              value={form.ref_type}
              onChange={(e) => update({ ref_type: e.target.value })}
              placeholder="e.g. game_key"
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth
              label="Ref id"
              value={form.ref_id}
              onChange={(e) => update({ ref_id: e.target.value })}
              placeholder="e.g. super_over"
            />
          </Grid>

          {CHALLENGE_TYPES.includes(form.activity_type) && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Hero media config</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Section title"
                  value={form.config.section_title || ''}
                  onChange={(e) => updateConfig({ section_title: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="CTA label"
                  value={form.config.cta_label || ''}
                  onChange={(e) => updateConfig({ cta_label: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Deeplink"
                  value={form.config.deeplink || ''}
                  onChange={(e) => updateConfig({ deeplink: e.target.value })}
                  required={form.activity_type === 'quiz'}
                  helperText={form.activity_type === 'quiz' ? 'Required for quiz cards' : ''}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Icon URL"
                  value={form.config.icon_url || ''}
                  onChange={(e) => updateConfig({ icon_url: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Config image URL"
                  value={form.config.image_url || ''}
                  onChange={(e) => updateConfig({ image_url: e.target.value })}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="BG color 1"
                  value={bg[0] || ''}
                  onChange={(e) => updateConfig({ background_color: [e.target.value, bg[1] || ''] })}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="BG color 2"
                  value={bg[1] || ''}
                  onChange={(e) => updateConfig({ background_color: [bg[0] || '', e.target.value] })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Subtitle (open)"
                  value={form.config.subtitle_open || ''}
                  onChange={(e) => updateConfig({ subtitle_open: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Subtitle (completed)"
                  value={form.config.subtitle_completed || ''}
                  onChange={(e) => updateConfig({ subtitle_completed: e.target.value })}
                />
              </Grid>
            </>
          )}

          {isOpinion && (
            <>
              <Grid item xs={12}>
                <Typography variant="subtitle2">Prediction / hot take config</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Section title"
                  value={form.config.section_title || ''}
                  onChange={(e) => updateConfig({ section_title: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Locks at (ISO)"
                  value={form.config.locks_at || ''}
                  onChange={(e) => updateConfig({ locks_at: e.target.value })}
                  placeholder="2026-11-03T14:00:00+05:30"
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Context label"
                  value={form.config.context?.label || ''}
                  onChange={(e) =>
                    updateConfig({
                      context: { ...(form.config.context || {}), label: e.target.value },
                    })
                  }
                  placeholder="INDvsNZ"
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="BG color 1"
                  value={bg[0] || ''}
                  onChange={(e) => updateConfig({ background_color: [e.target.value, bg[1] || ''] })}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="BG color 2"
                  value={bg[1] || ''}
                  onChange={(e) => updateConfig({ background_color: [bg[0] || '', e.target.value] })}
                />
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="subtitle2">Context images (0–3)</Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    disabled={contextImages.length >= 3}
                    onClick={() =>
                      updateConfig({
                        context: {
                          ...(form.config.context || {}),
                          images: [...contextImages, { url: '' }],
                        },
                      })
                    }
                  >
                    Add image
                  </Button>
                </Box>
                {contextImages.map((img, idx) => (
                  <Box key={idx} display="flex" gap={1} mt={1}>
                    <TextField
                      fullWidth
                      size="small"
                      label={`Image ${idx + 1} URL`}
                      value={img.url || ''}
                      onChange={(e) => {
                        const next = contextImages.map((row, i) =>
                          i === idx ? { url: e.target.value } : row
                        );
                        updateConfig({
                          context: { ...(form.config.context || {}), images: next },
                        });
                      }}
                    />
                    <IconButton
                      onClick={() =>
                        updateConfig({
                          context: {
                            ...(form.config.context || {}),
                            images: contextImages.filter((_, i) => i !== idx),
                          },
                        })
                      }
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                ))}
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Typography variant="subtitle2">Options (1–6)</Typography>
                  <Button
                    size="small"
                    startIcon={<AddIcon />}
                    disabled={options.length >= 6}
                    onClick={() =>
                      updateConfig({
                        options: [
                          ...options,
                          { id: `opt_${String.fromCharCode(97 + options.length)}`, label: '', image_url: '' },
                        ],
                      })
                    }
                  >
                    Add option
                  </Button>
                </Box>
                {options.map((opt, idx) => (
                  <Grid container spacing={1} key={opt.id || idx} sx={{ mt: 0.5 }} alignItems="center">
                    <Grid item xs={3}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Id"
                        value={opt.id || ''}
                        onChange={(e) => {
                          const next = options.map((row, i) =>
                            i === idx ? { ...row, id: e.target.value } : row
                          );
                          updateConfig({ options: next });
                        }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Label"
                        value={opt.label || ''}
                        onChange={(e) => {
                          const next = options.map((row, i) =>
                            i === idx ? { ...row, label: e.target.value } : row
                          );
                          updateConfig({ options: next });
                        }}
                      />
                    </Grid>
                    <Grid item xs={4}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Image URL"
                        value={opt.image_url || ''}
                        onChange={(e) => {
                          const next = options.map((row, i) =>
                            i === idx ? { ...row, image_url: e.target.value } : row
                          );
                          updateConfig({ options: next });
                        }}
                      />
                    </Grid>
                    <Grid item xs={1}>
                      <IconButton
                        disabled={options.length <= 1}
                        onClick={() =>
                          updateConfig({ options: options.filter((_, i) => i !== idx) })
                        }
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Grid>
                  </Grid>
                ))}
              </Grid>
            </>
          )}

          {locked && (
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={!!form.force_correction}
                    onChange={(e) => update({ force_correction: e.target.checked })}
                  />
                }
                label="Force correction (audited; never claws back credited FE)"
              />
            </Grid>
          )}
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="contained" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ActivityFormModal;
